import React, { Component } from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer, Tooltip } from 'recharts';
const RisksOpportunitiesDashboard: React.FC = () => {
  // Section A - Risk Indicators
  const riskIndicators = [{
    title: 'Low OEE Alert',
    trigger: 'OEE < Target',
    current: '78.5%',
    target: '85.0%',
    gap: '-6.5%',
    isRisk: true,
    severity: 'Medium'
  }, {
    title: 'High Scrap Cost',
    current: '₹48 Cr',
    rate: '2.1%',
    change: '+0.3%',
    isRisk: true,
    severity: 'High'
  }, {
    title: 'Warranty Spike',
    current: '₹185 Cr',
    previous: '₹181 Cr',
    claimsPer1000: '12.5',
    change: '+2.2%',
    isRisk: true,
    severity: 'High'
  }, {
    title: 'Supply Chain Risk',
    delayedShipments: '8.5%',
    highLeadTimeParts: '24',
    isRisk: true,
    severity: 'Medium'
  }, {
    title: 'Working Capital Pressure',
    dio: '42 days',
    dso: '58 days',
    dpo: '54 days',
    ccc: '46 days',
    isRisk: false,
    severity: 'Low'
  }];
  // Section B - Risk Radar Data
  const riskRadarData = [{
    metric: 'OEE Gap',
    value: 75,
    fullMark: 100
  }, {
    metric: 'Scrap Severity',
    value: 82,
    fullMark: 100
  }, {
    metric: 'Warranty Impact',
    value: 85,
    fullMark: 100
  }, {
    metric: 'Supply Chain',
    value: 68,
    fullMark: 100
  }, {
    metric: 'Financial Stress',
    value: 45,
    fullMark: 100
  }];
  // Section C - Risk Heatmap Data
  const riskHeatmapData = [{
    plant: 'Manesar',
    oeeGap: 8.5,
    scrapRate: 2.1,
    warrantyRate: 1.8,
    otd: 91.5,
    auditScore: 92
  }, {
    plant: 'Chennai',
    oeeGap: 3.0,
    scrapRate: 1.8,
    warrantyRate: 1.5,
    otd: 94.2,
    auditScore: 94
  }, {
    plant: 'Pune',
    oeeGap: 10.0,
    scrapRate: 2.4,
    warrantyRate: 2.1,
    otd: 88.5,
    auditScore: 90
  }, {
    plant: 'Bangalore',
    oeeGap: 6.0,
    scrapRate: 1.9,
    warrantyRate: 1.7,
    otd: 92.8,
    auditScore: 93
  }];
  // Section D - Opportunities
  const opportunities = [{
    title: 'Fast-Growing Product Lines',
    description: 'LED Headlamps growing at 24% YoY, ADAS components at 18% YoY',
    impact: '₹285 Cr incremental revenue',
    icon: <TrendingUpIcon size={20} data-id="element-3313" />,
    color: 'from-green-500 to-emerald-600'
  }, {
    title: 'High-Margin Customers',
    description: 'Top 3 customers delivering 18.5% avg margin vs 14.8% company avg',
    impact: '₹142 Cr margin contribution',
    icon: <CheckCircleIcon size={20} data-id="element-3314" />,
    color: 'from-blue-500 to-indigo-600'
  }, {
    title: 'Rising Plant Utilization',
    description: 'Chennai plant at 88% utilization, Bangalore at 86%',
    impact: '₹95 Cr capacity headroom',
    icon: <TrendingUpIcon size={20} data-id="element-3315" />,
    color: 'from-purple-500 to-pink-600'
  }, {
    title: 'Declining Warranty Trends',
    description: 'Electrical products warranty down 12% QoQ',
    impact: '₹18 Cr cost savings',
    icon: <CheckCircleIcon size={20} data-id="element-3316" />,
    color: 'from-teal-500 to-cyan-600'
  }, {
    title: 'Process Improvements',
    description: 'New tooling reducing cycle time by 8%, scrap by 0.4%',
    impact: '₹32 Cr annual savings',
    icon: <TrendingUpIcon size={20} data-id="element-3317" />,
    color: 'from-yellow-500 to-orange-600'
  }, {
    title: 'Export Revenue Growth',
    description: 'Export revenue up 15% YoY, new markets opening',
    impact: '₹125 Cr incremental revenue',
    icon: <TrendingUpIcon size={20} data-id="element-3318" />,
    color: 'from-indigo-500 to-purple-600'
  }];
  // Section E - Top 5 Risks
  const top5Risks = [{
    rank: 1,
    risk: 'Warranty Cost Escalation',
    impact: '₹52 Cr additional cost',
    probability: '75%',
    severity: 'High'
  }, {
    rank: 2,
    risk: 'Material Cost Inflation',
    impact: '₹185 Cr margin erosion',
    probability: '70%',
    severity: 'High'
  }, {
    rank: 3,
    risk: 'OEE Below Target - Pune Plant',
    impact: '₹42 Cr productivity loss',
    probability: '80%',
    severity: 'Medium'
  }, {
    rank: 4,
    risk: 'Supply Chain Delays',
    impact: '₹28 Cr production loss',
    probability: '65%',
    severity: 'Medium'
  }, {
    rank: 5,
    risk: 'Customer Concentration',
    impact: '₹1,710 Cr exposure',
    probability: '55%',
    severity: 'Medium'
  }];
  // Top 5 Opportunities
  const top5Opportunities = [{
    rank: 1,
    opportunity: 'EV Component Market Entry',
    potential: '₹285 Cr',
    probability: '75%',
    timeline: '18-24 months'
  }, {
    rank: 2,
    opportunity: 'Chennai Efficiency Replication',
    potential: '₹142 Cr',
    probability: '85%',
    timeline: '6-9 months'
  }, {
    rank: 3,
    opportunity: 'Export Market Expansion',
    potential: '₹125 Cr',
    probability: '70%',
    timeline: '12-18 months'
  }, {
    rank: 4,
    opportunity: 'Premium Product Mix Shift',
    potential: '₹95 Cr',
    probability: '80%',
    timeline: '6-12 months'
  }, {
    rank: 5,
    opportunity: 'Vendor Consolidation',
    potential: '₹68 Cr',
    probability: '90%',
    timeline: '3-6 months'
  }];
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'High':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'Low':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };
  const getHeatmapColor = (value: number, metric: string) => {
    if (metric === 'oeeGap' || metric === 'scrapRate' || metric === 'warrantyRate') {
      // Lower is better
      if (value <= 3) return 'bg-green-100 text-green-800';
      if (value <= 7) return 'bg-yellow-100 text-yellow-800';
      return 'bg-red-100 text-red-800';
    } else {
      // Higher is better
      if (value >= 93) return 'bg-green-100 text-green-800';
      if (value >= 90) return 'bg-yellow-100 text-yellow-800';
      return 'bg-red-100 text-red-800';
    }
  };
  return <div data-id="element-3319">
      <DescriptionPanel title="Risks & Opportunities Analysis" description="Comprehensive view of operational risks, quality issues, supply chain challenges, and strategic opportunities. Monitor risk indicators, identify improvement areas, and capitalize on growth opportunities to drive business performance." insights={['Warranty cost escalation flagged as top risk - ₹52 Cr potential impact', 'EV component market entry represents ₹285 Cr opportunity with 75% probability', 'Pune plant OEE 10% below target - immediate intervention required', 'Chennai plant efficiency model can unlock ₹142 Cr across network']} data-id="element-3320" />
      {/* SECTION A - Risk Indicators */}
      <div className="mb-8" data-id="element-3321">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-3322">
          Risk Indicators
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-3323">
          {riskIndicators.map((indicator, index) => <Card key={index} className={`p-5 ${indicator.isRisk ? 'border-2 border-red-200 bg-red-50' : 'border-2 border-green-200 bg-green-50'}`} data-id="element-3324">
              <div className="flex items-start justify-between mb-3" data-id="element-3325">
                <div className="text-xs font-bold text-gray-700 uppercase tracking-wide" data-id="element-3326">
                  {indicator.title}
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${getSeverityColor(indicator.severity)}`} data-id="element-3327">
                  {indicator.severity}
                </span>
              </div>
              {indicator.title === 'Low OEE Alert' && <>
                  <div className="text-xs text-gray-600 mb-2" data-id="element-3328">
                    {indicator.trigger}
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3329">
                    {indicator.current}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-3330">
                    Target: {indicator.target}
                  </div>
                  <div className="text-sm font-bold text-red-600 mt-2" data-id="element-3331">
                    Gap: {indicator.gap}
                  </div>
                </>}
              {indicator.title === 'High Scrap Cost' && <>
                  <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3332">
                    {indicator.current}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-3333">
                    Rate: {indicator.rate}
                  </div>
                  <div className="text-sm font-bold text-red-600 mt-2 flex items-center" data-id="element-3334">
                    <TrendingUpIcon size={14} className="mr-1" data-id="element-3335" />
                    {indicator.change}
                  </div>
                </>}
              {indicator.title === 'Warranty Spike' && <>
                  <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3336">
                    {indicator.current}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-3337">
                    Previous: {indicator.previous}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-3338">
                    Claims/1000: {indicator.claimsPer1000}
                  </div>
                  <div className="text-sm font-bold text-red-600 mt-2 flex items-center" data-id="element-3339">
                    <TrendingUpIcon size={14} className="mr-1" data-id="element-3340" />
                    {indicator.change}
                  </div>
                </>}
              {indicator.title === 'Supply Chain Risk' && <>
                  <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3341">
                    {indicator.delayedShipments}
                  </div>
                  <div className="text-xs text-gray-600 mb-2" data-id="element-3342">
                    Delayed Shipments
                  </div>
                  <div className="text-sm font-semibold text-gray-900" data-id="element-3343">
                    {indicator.highLeadTimeParts}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-3344">
                    High Lead Time Parts
                  </div>
                </>}
              {indicator.title === 'Working Capital Pressure' && <>
                  <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-3345">
                    <div data-id="element-3346">
                      <div className="text-gray-600" data-id="element-3347">DIO</div>
                      <div className="font-bold text-gray-900" data-id="element-3348">
                        {indicator.dio}
                      </div>
                    </div>
                    <div data-id="element-3349">
                      <div className="text-gray-600" data-id="element-3350">DSO</div>
                      <div className="font-bold text-gray-900" data-id="element-3351">
                        {indicator.dso}
                      </div>
                    </div>
                    <div data-id="element-3352">
                      <div className="text-gray-600" data-id="element-3353">DPO</div>
                      <div className="font-bold text-gray-900" data-id="element-3354">
                        {indicator.dpo}
                      </div>
                    </div>
                    <div data-id="element-3355">
                      <div className="text-gray-600" data-id="element-3356">CCC</div>
                      <div className="font-bold text-green-700" data-id="element-3357">
                        {indicator.ccc}
                      </div>
                    </div>
                  </div>
                </>}
            </Card>)}
        </div>
      </div>
      {/* SECTION B & C - Risk Radar and Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-3358">
        {/* Risk Radar */}
        <Card className="p-6" data-id="element-3359">
          <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-3360">Risk Radar</h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-3361">
            Multi-dimensional risk assessment
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-3362">
            <RadarChart data={riskRadarData} data-id="element-3363">
              <PolarGrid stroke="#e5e7eb" data-id="element-3364" />
              <PolarAngleAxis dataKey="metric" tick={{
              fontSize: 11
            }} data-id="element-3365" />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{
              fontSize: 10
            }} data-id="element-3366" />
              <Radar name="Risk Level" dataKey="value" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} strokeWidth={2} data-id="element-3367" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-3368" />
            </RadarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-3369">
            <div className="text-sm text-gray-600" data-id="element-3370">
              <span className="font-semibold" data-id="element-3371">Highest Risk:</span> Warranty
              Impact (85/100)
            </div>
            <div className="text-sm text-gray-600" data-id="element-3372">
              <span className="font-semibold" data-id="element-3373">Lowest Risk:</span> Financial
              Stress (45/100)
            </div>
          </div>
        </Card>
        {/* Risk Heatmap */}
        <Card className="p-6" data-id="element-3374">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3375">
            Risk Hotspots by Plant
          </h3>
          <div className="overflow-x-auto" data-id="element-3376">
            <table className="w-full text-sm" data-id="element-3377">
              <thead data-id="element-3378">
                <tr className="border-b-2 border-gray-300" data-id="element-3379">
                  <th className="text-left py-2 px-3 font-bold text-gray-700" data-id="element-3380">
                    Plant
                  </th>
                  <th className="text-center py-2 px-3 font-bold text-gray-700" data-id="element-3381">
                    OEE Gap
                  </th>
                  <th className="text-center py-2 px-3 font-bold text-gray-700" data-id="element-3382">
                    Scrap %
                  </th>
                  <th className="text-center py-2 px-3 font-bold text-gray-700" data-id="element-3383">
                    Warranty %
                  </th>
                  <th className="text-center py-2 px-3 font-bold text-gray-700" data-id="element-3384">
                    OTD %
                  </th>
                  <th className="text-center py-2 px-3 font-bold text-gray-700" data-id="element-3385">
                    Audit
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-3386">
                {riskHeatmapData.map((row, index) => <tr key={index} className="border-b border-gray-100" data-id="element-3387">
                    <td className="py-2 px-3 font-medium text-gray-900" data-id="element-3388">
                      {row.plant}
                    </td>
                    <td className="py-2 px-3 text-center" data-id="element-3389">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${getHeatmapColor(row.oeeGap, 'oeeGap')}`} data-id="element-3390">
                        {row.oeeGap}%
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center" data-id="element-3391">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${getHeatmapColor(row.scrapRate, 'scrapRate')}`} data-id="element-3392">
                        {row.scrapRate}%
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center" data-id="element-3393">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${getHeatmapColor(row.warrantyRate, 'warrantyRate')}`} data-id="element-3394">
                        {row.warrantyRate}%
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center" data-id="element-3395">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${getHeatmapColor(row.otd, 'otd')}`} data-id="element-3396">
                        {row.otd}%
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center" data-id="element-3397">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${getHeatmapColor(row.auditScore, 'audit')}`} data-id="element-3398">
                        {row.auditScore}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-3399">
            <div className="flex items-center space-x-4 text-xs" data-id="element-3400">
              <div className="flex items-center" data-id="element-3401">
                <div className="w-4 h-4 bg-green-100 rounded mr-1" data-id="element-3402" />
                <span data-id="element-3403">Good</span>
              </div>
              <div className="flex items-center" data-id="element-3404">
                <div className="w-4 h-4 bg-yellow-100 rounded mr-1" data-id="element-3405" />
                <span data-id="element-3406">Warning</span>
              </div>
              <div className="flex items-center" data-id="element-3407">
                <div className="w-4 h-4 bg-red-100 rounded mr-1" data-id="element-3408" />
                <span data-id="element-3409">Critical</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* SECTION D - Opportunities */}
      <div className="mb-8" data-id="element-3410">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-3411">
          Strategic Opportunities
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3412">
          {opportunities.map((opp, index) => <Card key={index} className={`p-6 bg-gradient-to-br ${opp.color} text-white`} data-id="element-3413">
              <div className="mb-3" data-id="element-3414">{opp.icon}</div>
              <h4 className="text-lg font-bold mb-2" data-id="element-3415">{opp.title}</h4>
              <p className="text-sm mb-4 text-white/90" data-id="element-3416">{opp.description}</p>
              <div className="text-sm font-bold bg-white/20 rounded px-3 py-1 inline-block" data-id="element-3417">
                {opp.impact}
              </div>
            </Card>)}
        </div>
      </div>
      {/* SECTION E - Top 5 Risks and Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3418">
        {/* Top 5 Risks */}
        <Card className="p-6 border-2 border-red-100 bg-red-50" data-id="element-3419">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3420">
            Top 5 Risks (Auto-Ranked)
          </h3>
          <div className="space-y-3" data-id="element-3421">
            {top5Risks.map((risk, index) => <div key={index} className="bg-white rounded-lg p-4 border-2 border-red-200" data-id="element-3422">
                <div className="flex items-start justify-between mb-2" data-id="element-3423">
                  <div className="flex items-start flex-1" data-id="element-3424">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-700 font-bold text-sm mr-3" data-id="element-3425">
                      #{risk.rank}
                    </div>
                    <div className="flex-1" data-id="element-3426">
                      <div className="font-bold text-sm text-gray-900 mb-1" data-id="element-3427">
                        {risk.risk}
                      </div>
                      <div className="text-xs text-gray-600" data-id="element-3428">{risk.impact}</div>
                    </div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded ${getSeverityColor(risk.severity)}`} data-id="element-3429">
                    {risk.severity}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-600 pt-2 border-t border-gray-200" data-id="element-3430">
                  <span data-id="element-3431">Probability: {risk.probability}</span>
                </div>
              </div>)}
          </div>
        </Card>
        {/* Top 5 Opportunities */}
        <Card className="p-6 border-2 border-green-100 bg-green-50" data-id="element-3432">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3433">
            Top 5 Opportunities (Auto-Ranked)
          </h3>
          <div className="space-y-3" data-id="element-3434">
            {top5Opportunities.map((opp, index) => <div key={index} className="bg-white rounded-lg p-4 border-2 border-green-200" data-id="element-3435">
                <div className="flex items-start justify-between mb-2" data-id="element-3436">
                  <div className="flex items-start flex-1" data-id="element-3437">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-bold text-sm mr-3" data-id="element-3438">
                      #{opp.rank}
                    </div>
                    <div className="flex-1" data-id="element-3439">
                      <div className="font-bold text-sm text-gray-900 mb-1" data-id="element-3440">
                        {opp.opportunity}
                      </div>
                      <div className="text-xs text-gray-600" data-id="element-3441">
                        Potential: {opp.potential}
                      </div>
                    </div>
                  </div>
                  <span className="text-xs font-bold px-2 py-1 rounded bg-green-100 text-green-700" data-id="element-3442">
                    {opp.probability}
                  </span>
                </div>
                <div className="text-xs text-gray-600 pt-2 border-t border-gray-200" data-id="element-3443">
                  Timeline: {opp.timeline}
                </div>
              </div>)}
          </div>
        </Card>
      </div>
    </div>;
};
export default RisksOpportunitiesDashboard;